import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../services/api";

interface Group {
  _id: string;
  name: string;
  description: string;
  recipients: any[];
  createdAt: string;
  updatedAt: string;
}

interface GroupsState {
  groups: Group[];
  loading: boolean;
  error: string | null;
}

const initialState: GroupsState = {
  groups: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchGroups = createAsyncThunk(
  "groups/fetchGroups",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getGroups();
      // The API returns { success: true, data: { groups: [], totalCount: 0, ... } }
      return (response as any).data || response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch groups");
    }
  }
);

export const createGroup = createAsyncThunk(
  "groups/createGroup",
  async (
    groupData: { name: string; description: string; recipients: any[] },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiService.createGroup(groupData);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create group");
    }
  }
);

export const updateGroup = createAsyncThunk(
  "groups/updateGroup",
  async (
    {
      id,
      ...groupData
    }: { id: string; name: string; description: string; recipients: any[] },
    { rejectWithValue }
  ) => {
    if (!id || id === "undefined") {
      console.error("updateGroup called with invalid id:", id);
      return rejectWithValue("Invalid group ID");
    }
    try {
      const response = await apiService.updateGroup(id, groupData);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update group");
    }
  }
);

export const deleteGroup = createAsyncThunk(
  "groups/deleteGroup",
  async (id: string, { rejectWithValue }) => {
    if (!id || id === "undefined") {
      console.error("deleteGroup called with invalid id:", id);
      return rejectWithValue("Invalid group ID for deletion");
    }
    try {
      await apiService.deleteGroup(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete group");
    }
  }
);

const groupsSlice = createSlice({
  name: "groups",
  initialState,
  reducers: {
    clearGroupsError: (state) => {
      state.error = null;
    },
    clearGroups: (state) => {
      state.groups = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch groups
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.groups || action.payload || [];
      })
      .addCase(fetchGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create group
    builder
      .addCase(createGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups.push(action.payload);
      })
      .addCase(createGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update group
    builder
      .addCase(updateGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(
          (group) => group._id === action.payload._id
        );
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
      })
      .addCase(updateGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete group
    builder
      .addCase(deleteGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = state.groups.filter((group) => {
          const groupId = group._id || (group as any).id;
          return groupId !== action.payload;
        });
      })
      .addCase(deleteGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearGroupsError, clearGroups } = groupsSlice.actions;
export default groupsSlice.reducer;
